import { useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Speaker } from "src/types";

import type { SpeakerCardProps as CrosbySpeakerCardProps } from "@/components/editor/themes/crosby/blocks/speakers/speaker-card/speaker-card-component";
import type { SpeakerCardProps as SeldonSpeakerCardProps } from "@/components/editor/themes/starter-hub/blocks/speakers/speaker-card/speaker-card-component";
import { People } from "@/components/icons/people";
import ImportBase from "@/components/import-list/import-base-component";

type SpeakersSettingsProps = {
  template: React.FunctionComponent<
    SeldonSpeakerCardProps | CrosbySpeakerCardProps
  >;
};

export default function SpeakersSettings(props: SpeakersSettingsProps) {
  const {
    actions: { addNodeTree },
    query,
  } = useEditor();

  const { t } = useTranslation();
  const { nodeId } = useNode((node) => {
    const nodeId = node
      .getLinkedNodes()
      .find((node) => node.id === "speakers-list")?.node.id;

    return {
      nodeId: nodeId || node.id,
    };
  });

  const handleImportSpeakers = React.useCallback(
    (speakers: Speaker[]) => {
      if (!nodeId) return;
      if (!speakers?.length) return;
      // Add a SpeakerCard for each speaker imported
      speakers.forEach((speaker) => {
        const nodeTree = query.parseReactElementAsNodeTree(
          React.createElement(props.template, {
            speaker,
            isBioAvailable: !!speaker.bio,
          })
        );
        addNodeTree(nodeTree, nodeId);
      });
    },
    [query, props.template, addNodeTree, nodeId]
  );

  return (
    <ImportBase
      title={t("preference-panel.speakers.speakers")}
      type="speakers"
      icon={People}
      importText={t("preference-panel.speakers.import-speakers-from-event")}
      importButtonText={t("preference-panel.speakers.import-speakers")}
      onImportClick={handleImportSpeakers}
      mainEmptyText={t("preference-panel.speakers.add-speakers")}
      subEmptyText={t("preference-panel.speakers.add-speakers-details")}
    />
  );
}
